/* eslint-disable clubmed/no-fake-component */
import React, { Fragment, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Collapse } from 'react-collapse';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Text } from 'Components/frameworks/text/Text';
import { ServiceBlockCTA } from './ServiceBlockCTA';
type Slot = {
  disclaimer?: string;
  icon: IconName;
  key?: string;
  label: (string | null | undefined) | Node;
};
type Props = {
  addLabel: string;
  button?: {
    dataCsOverrideId?: string;
    isLoading?: boolean;
    label?: string;
    onClick?: () => Promise<any> | void;
    onClose?: () => void;
  };
  attendeeId?: string;
  showOnlyCurrentAttendeeSlots?: boolean;
  className?: string;
  dataSelector: string;
  equipment:
    | Equipment
    | {
        id: string;
        label: string;
        longDescription: string | Node;
      };
  header: {
    color: string;
    header: 'large' | 'small';
    icon: IconName;
    label: string;
  };
  image?: string;
  includedLabel: string;
  intro?: string;
  isPriceForAdultOnly?: boolean;
  isValidated?: boolean;
  lastAvailabilities?: Node;
  price: string;
  removeLabel: string;
  slots: Array<Slot>;
  style?: CSSRuleList;
  trackingData?: TrackingData;
  type?: 'add' | 'remove' | 'disabled' | 'none' | 'edit' | 'full';
  urgentisation?: string | Node;
  rentedAmount?: string;
};
export const ServiceBlock = ({
  addLabel = '',
  button,
  className,
  dataSelector = 'ServiceBlock',
  equipment,
  header,
  image,
  includedLabel = '',
  intro,
  isPriceForAdultOnly,
  isValidated,
  lastAvailabilities,
  price = '',
  removeLabel = '',
  slots,
  style,
  type = 'add',
  urgentisation,
  rentedAmount,
}: Props) => {
  const [shouldDisplayMoreDetails, setShouldDisplayMoreDetails] = useState(false);
  const isDisabled = type === 'disabled';
  const isFull = type === 'full';
  const additionalStyle = {
    label: {
      fontSize: '14px',
    },
    icon: {
      display: 'flex',
      width: '1rem',
    },
  };

  const toggleDisplayMoreDetails = () => {
    setShouldDisplayMoreDetails(!shouldDisplayMoreDetails);
  };

  return (
    <div
      className={classnames(className, 'isolate flex-none py-5')}
      data-testid="ServiceBlock"
      style={style}
    >
      <Capsule {...header} additionalStyle={additionalStyle}>
        {!!image && (
          <div className="relative isolate z-1">
            <div
              className={classnames('bg-cover bg-center bg-no-repeat', {
                'opacity-1 pt-[calc((4/7)*100%)]': !shouldDisplayMoreDetails,
                'opacity-0': shouldDisplayMoreDetails,
              })}
              style={{
                backgroundImage: `url(${image})`,
                transition: 'padding-top 0.2s linear, opacity 0.2s linear',
              }}
            />
            {!shouldDisplayMoreDetails ? lastAvailabilities : null}
          </div>
        )}
        <div className="relative p-10 font-sans text-14">
          {isValidated && !isFull ? (
            <div className="relative">
              <div className="absolute left-[-23px] right-[-23px] top-[-26px] flex justify-end">
                <Icon className="text-success" name={ICONS.CHECK_OUTLINE} width="3.75rem" />
              </div>
            </div>
          ) : null}
          <h3 className="font-brand text-20 font-bold leading-1 text-black">{equipment.label}</h3>
          {slots?.length > 0 && (
            <ul className="pt-10">
              {slots.map((slot) => (
                <li
                  className="flex gap-10 py-5"
                  data-testid={slot.key || slot.label}
                  key={slot.key || slot.label}
                >
                  <Icon className="shrink-0 text-sienna" name={slot.icon} width="1.5rem" />
                  <div>
                    <p className="text-14">{slot.label}</p>
                    {!!slot.disclaimer && <p className="text-12">{slot.disclaimer}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {equipment.longDescription ? (
            <Fragment>
              <Collapse
                theme={{
                  content: 'flex flex-col gap-10',
                }}
                isOpened={shouldDisplayMoreDetails}
              >
                {intro ? <p className="text-12">{intro}</p> : null}
                {typeof equipment.longDescription === 'string' ? (
                  <Text text={equipment.longDescription} theme="text-sans-n2" />
                ) : (
                  equipment.longDescription
                )}
              </Collapse>
              <button
                className="ms-auto inline-flex items-center gap-5 py-10 text-14 text-black underline"
                onClick={toggleDisplayMoreDetails}
                type="button"
              >
                {shouldDisplayMoreDetails ? (
                  <Fragment>
                    {t('common.loadLess')}
                    &nbsp;
                    <Icon name={ICONS.MINUS} width="0.625rem" />
                  </Fragment>
                ) : (
                  <Fragment>
                    {t('common.loadMore')}
                    &nbsp;
                    <Icon name={ICONS.PLUS} width="0.625rem" />
                  </Fragment>
                )}
              </button>
            </Fragment>
          ) : null}
          {urgentisation && !isFull && (
            <div className="flex items-center justify-center" data-testid="urgentisationBlock">
              {urgentisation}
            </div>
          )}
          {isFull && t('booking.services.childcare.babySittingService') !== '' && (
            <p className="mb-15 text-12 text-orange">
              {t('booking.services.childcare.babySittingService')}
            </p>
          )}
          <ServiceBlockCTA
            addLabel={addLabel}
            button={button}
            dataSelector={dataSelector}
            equipment={equipment}
            includedLabel={includedLabel}
            price={type === 'remove' || type === 'edit' ? rentedAmount : price}
            removeLabel={removeLabel}
            type={type}
          />
          {!isDisabled && !isFull && button && (
            <p className="mt-10 text-center text-10">
              {isPriceForAdultOnly
                ? t('booking.common.pricePerAdult')
                : t('booking.common.pricePerPerson')}
            </p>
          )}
        </div>
      </Capsule>
    </div>
  );
};
